import type { queueInterface } from '@/types/viewsType/socketType'
import type {
  memberListInterface,
  charmValueMapInterface
} from '@/types/apiType/roomType'
import type {
  volumeIndicatorSDKInterface,
  volumeIndicatorMapInterface
} from '@/types/viewsType/roomType'
import { apiLockMic } from '~/api/room'

export const useAudioStore = defineStore({
  id: 'audio',
  state: () => ({
    isOpenSound:
      !(common.checkVersion().ios || common.checkVersion().webView) ||
      common.checkVersion().app, // 是否开启扬声器 ,IOS || webView 默认false，appWebView默认开启,其他true
    isOpenLocalMin: true, // 是否打开本地麦克风, isOpenLocalMin 开启了，isOpenMic才可以开启
    isOpenMic: true, // 是否开启麦克风
    isOnMicSeat: false, //是否已经在麦上
    isLockMic: false, //自己是否被禁麦
    isManager: false, //是否是管理员
    isCreator: false, //是否是房主
    micTotalCount: 10, //麦位数量
    hostMinInfo: common.defaultHostMinInfo as queueInterface, // 主播麦位信息
    queueList: common.defaultQueueList as queueInterface[], //麦位列表
    // charmList: {}, //麦位魅力值 { uid: value }
    inMicMembers: [] as memberListInterface[], //在麦成员
    volumeIndicatorMap: {} as volumeIndicatorMapInterface, // 音量指标数组
    charmValueMap: {} as charmValueMapInterface, // 用户麦位魅力值
    curSelectMicPosition: 0 as number // 当前选中麦位(邀请上麦用)
  }),
  getters: {
    // 主播是否在1-20号麦位上
    isHostInMic(state) {
      const roomStore = useRoomStore()
      return state.queueList.some(
        (item) => item.value.member?.uid === roomStore.anchorInfo.uid
      )
    }
  },
  actions: {
    // 初始化麦位列表等信息
    initData(data) {
      const userStore = useUserStore()
      const roomStore = useRoomStore()

      this.micTotalCount = data.room_info.micTotalCount || 10 //麦位数量
      this.queueList = data.queue_list.slice(1) //麦位列表
      this.hostMinInfo = data.queue_list[0] // 主播麦位
      this.charmValueMap = {} // 用户麦位魅力值

      // 更新自己的状态字段
      const queue = data.queue_list.find(
        (v) => v.value.member?.uid === userStore.userInfo.uid
      )
      this.isOnMicSeat = !!queue //用户自己是否在麦位上
      this.isManager = data.member.is_manager //用户自己是否是管理员
      this.isCreator = data.member.is_creator //用户自己是否是房主
      this.isLockMic =
        queue && this.getMicInfoByKey(queue.key).value.mic_info.micState
          ? true
          : false // 是否被禁麦  0:开麦， 1：锁麦
      this.isOpenMic = !this.isLockMic

      // 如果房主不在麦上，自动上麦
      if (this.isCreator && !this.isOnMicSeat) {
        if (!roomStore.roomId) {
          roomStore.setRoomAppId(data.room_info.roomId, roomStore.roomUid)
        }
        this.updateQueue(-1)
      }

      // 如果主播麦位信息member为空，尝试从列表中获取
      if (!this.hostMinInfo.value.member) {
        const member = this.queueList.find(
          (item) => item.value.member?.uid === roomStore.roomUid
        )?.value.member
        if (member) {
          this.hostMinInfo.value.member = member
        }
      }

      // 过滤麦位列表,获取当前在麦成员
      this.getInMicMembers()
    },
    /**
     * 获取key 对应的麦位信息
     */
    getMicInfoByKey(key: number) {
      return key === -1 ? this.hostMinInfo : this.queueList[key]
    },
    /**
     * 获取音频初始化状态
     */
    getMicrophoneStatus() {
      const agoraStore = useAgoraStore()
      const trtcStore = useTrtcStore()
      const { roomInfo } = useRoomStore()
      if (roomInfo.audioChannel === 1) {
        if (!agoraStore.joined) return false
        return agoraStore.initedMicrophone
      } else if (roomInfo.audioChannel === 3) {
        if (!trtcStore.joined) return false
        return trtcStore.initedMicrophone
      }
    },
    /**
     * 设置音量指标数组
     * 将数组转对象,因腾讯和声网字段不一致，所以需要过滤
     */
    setVolumeIndicator(list: volumeIndicatorSDKInterface[]) {
      const userStore = useUserStore()
      const obj = {} as volumeIndicatorMapInterface
      list.forEach((v) => {
        let uid = Number(v.uid || v.userId || 0)
        const volume = v.level || v.volume || 0
        // 腾讯兼容性，如果是0，那就是用户自己
        if (uid === 0) {
          uid = userStore.userInfo.uid
        }
        obj[uid] = {
          uid,
          volume
        }
      })
      this.volumeIndicatorMap = obj
    },
    /**
     * 监听麦位数量发生变化
     */
    onMicTotalCountUpdate(room_info) {
      // 有返回micTotalCount字段才赋值
      if (room_info.micTotalCount) {
        this.micTotalCount = room_info.micTotalCount //麦位数量
      }
    },
    // 麦位或主播位成员列表发生变化
    onQueueMemberUpdateNotice(data) {
      /*
        key 麦位排序
        min_info 麦位信息
        value 麦位用户信息
        type 1上麦 2下麦
        connect_mic_type 0语音 1视频 2invalid 3PK
      */
      const userStore = useUserStore()
      const { userInfo } = userStore
      const isSelf = data.value.uid === userInfo.uid

      // 遍历查询之前是否有在麦,有就在最后清除掉
      const queueFindValue = this.queueList.find(
        (v) => v.value.member && v.value.member.uid === data.value.uid
      )

      // 处理麦位信息
      if (data.key === -1) {
        // -1 是房主，特殊处理
        if (data.type === 1 && data.value) {
          this.hostMinInfo.value.member = data.value
        }
        if (data.mic_info) {
          this.hostMinInfo.value.mic_info = data.mic_info
        }
      } else {
        // 下麦清除操作在下麦后再清除
        if (data.type === 1) {
          this.queueList[data.key]?.value &&
            (this.queueList[data.key].value.member = data.value)
        }
        if (data.mic_info) {
          this.queueList[data.key]?.value &&
            (this.queueList[data.key].value.mic_info = data.mic_info)
        }
      }

      // 上下麦处理
      if (data.type === 1) {
        // 上麦处理
        //如果是自己上麦 就发布本地音频轨道
        if (isSelf) {
          this.isOnMicSeat = true
          this.isLockMic = this.getMicInfoByKey(data.key).value.mic_info
            .micState
            ? true
            : false // 是否被禁麦  0:开麦， 1：锁麦
          this.manageLocalAudioMicrophone(!this.isLockMic)
        }
      } else if (data.type === 2) {
        // 下麦处理
        //如果是自己下麦 就取消发布本地音频轨道
        if (
          data.key !== -1 &&
          this.queueList[data.key]?.value &&
          this.queueList[data.key].value.member?.uid == userInfo.uid
        ) {
          this.isOnMicSeat = false
          this.manageLocalAudioMicrophone(false)
        }
        // 清除麦位用户信息
        if (data.key === -1) {
          this.hostMinInfo.value.member = data.value = null
        } else {
          this.queueList[data.key]?.value &&
            (this.queueList[data.key].value.member = null)
        }
      }

      // 有就重置, 在最后才遍历
      if (queueFindValue) {
        // 查询到重置
        queueFindValue.value.member = null
      }

      // 过滤麦位列表,获取当前在麦成员
      this.getInMicMembers()

      // 如果是房主自己从麦位列表下麦，则自动上0号麦位
      if (this.isCreator && data.key !== -1 && data.type === 2 && isSelf) {
        this.updateQueue(-1)
      }
    },
    // 麦位魅力值推送通知
    onRoomMicCharmPush(data: charmValueMapInterface) {
      this.charmValueMap = data
    },
    //麦位状态更新
    onQueueMicUpdateNotice(data) {
      const { userInfo } = useUserStore()

      // 设置麦位状态
      if (data.key === -1) {
        // -1 是房主，特殊处理
        this.hostMinInfo.value.mic_info = data.min_info
      } else {
        // 其他麦位直接设置
        this.queueList[data.key]?.value &&
          (this.queueList[data.key].value.mic_info = data.mic_info)
      }

      // 如果是自己
      if (
        data.key !== -1 &&
        this.queueList[data.key]?.value.member?.uid === userInfo.uid
      ) {
        this.isLockMic = data.mic_info.micState ? true : false
        this.manageLocalAudioMicrophone(!this.isLockMic)
      }

      // 过滤麦位列表,获取当前在麦成员
      this.getInMicMembers()
    },
    /**
     * 上麦
     * index：位置
     * isInvite：是否邀请上麦，是就直接上麦
     * */
    updateQueue(index: number, isInvite?: boolean) {
      // 如果不是管理员角色 麦位被锁住了 return回去
      if (
        this.getMicInfoByKey(index).value.mic_info.posState &&
        !this.isManager &&
        !this.isCreator &&
        !isInvite
      )
        return

      const socketStore = useSocketStore()
      const userStore = useUserStore()
      const roomStore = useRoomStore()
      const { userInfo } = userStore
      const route = useRoute()

      const roomId = roomStore.roomId
      const position = index
      const uid = localStorage.getItem('loginInfo')
        ? JSON.parse(localStorage.getItem('loginInfo') as string).uid
        : userInfo.uid
      // 发送上麦socket
      socketStore.privateSend(
        {
          route: socketRequestRoute.updateQueue,
          req_data: {
            room_id: roomId,
            key: position,
            uid
          }
        },
        true
      )
    },
    // 下麦
    pollQueue(index) {
      const socketStore = useSocketStore()
      const roomStore = useRoomStore()
      const route = useRoute()

      const roomId = roomStore.roomId
      const position = index

      // 发送下麦socket
      socketStore.privateSend(
        {
          route: socketRequestRoute.pollQueue,
          req_data: {
            room_id: roomId,
            key: position
          }
        },
        true
      )
    },
    /**
     * 设置当前要邀请的麦位
     * @param pos 麦位
     */
    setInviteMicPosition(pos: number) {
      this.curSelectMicPosition = pos
    },
    /**
     * 禁麦/解锁麦
     * @param position 麦位 0-19
     * @param state 状态，1：锁麦，0开麦（即取消锁麦）
     */
    async lockMic(position: number, state: 1 | 0) {
      const roomStore = useRoomStore()
      const roomUid = roomStore.roomUid
      const type = roomStore.roomType
      return apiLockMic({ position, roomUid, state, type }).then((res) => {
        if (res.code !== 200) {
          showToast(res.message)
          return false
        }
        return true
      })
    },
    /**
     * 发起邀请用户上麦im
     */
    sendInviteUserUpMic(uid: number) {
      const roomStore = useRoomStore()
      const socketStore = useSocketStore()
      const req_data = {
        room_id: roomStore.roomId,
        custom: JSON.stringify({
          data: {
            micPosition: this.curSelectMicPosition,
            uid
          },
          first: 8,
          second: 81
        }),
        member: JSON.stringify({ ...socketStore.memberUserInfo })
      }
      socketStore.sendMessage(req_data)
    },
    // 过滤麦位列表,获取当前在麦成员
    getInMicMembers() {
      const arr: memberListInterface[] = []
      this.queueList.forEach((item) => {
        if (item.value.member) arr.push(item.value.member)
      })
      this.inMicMembers = arr
    },
    // 设置管理员通知
    onChatRoomManagerAdd(data) {
      const userStore = useUserStore()
      const { userInfo } = userStore
      const member = data.member
      this.queueList.forEach((item) => {
        if (item.value.member && item.value.member.uid === member.uid) {
          item.value.member.is_manager = true

          // 是自己的话
          if (userInfo.uid === member.uid) {
            this.isManager = true
            const i18n = useNuxtApp().$i18n
            showToast(i18n.t('room.setManageTips'))
          }
        }
      })
    },
    // 移除管理员通知
    onChatRoomManagerRemove(data) {
      const userStore = useUserStore()
      const { userInfo } = userStore
      const member = data.member
      this.queueList.forEach((item) => {
        if (item.value.member && item.value.member.uid === member.uid) {
          item.value.member.is_manager = false

          // 是自己的话
          if (userInfo.uid === member.uid) {
            this.isManager = false
            const i18n = useNuxtApp().$i18n
            showToast(i18n.t('room.removeManageTips'))
          }
        }
      })
    },
    // 邀请上麦通知
    onInviteMessage(data) {
      const userStore = useUserStore()
      const { userInfo } = userStore
      // 收到的是用户自己被邀请就上麦
      if (userInfo.uid == data.uid) {
        this.updateQueue(data.micPosition, true)
        // 自己邀请自己就不做提示
        if (userInfo.uid !== data.uid) {
          const i18n = useNuxtApp().$i18n
          showToast(i18n.t('room.inviteSuccessTips'))
        }
      }
    },
    // 开关声音
    switchAllRemoteAudioTrack(isOpenSound) {
      const { roomInfo } = useRoomStore()
      const agoraStore = useAgoraStore()
      const trtcStore = useTrtcStore()
      this.setIsOpenSound(isOpenSound)
      if (roomInfo.audioChannel === 1) {
        if (!agoraStore.joined) return
        agoraStore.switchAllRemoteAudioTrack(isOpenSound)
      } else if (roomInfo.audioChannel === 3) {
        if (!trtcStore.joined) return
        trtcStore.switchAllRemoteAudioTrack(isOpenSound)
      }
    },
    /**
     * 开关麦克风
     * this.isOpenLocalMin  本地打开，才可以打开麦克风
     * **/
    manageLocalAudioMicrophone(isOpenMic) {
      const { roomInfo } = useRoomStore()
      const agoraStore = useAgoraStore()
      const trtcStore = useTrtcStore()
      this.isOpenMic = this.isOpenLocalMin && isOpenMic
      if (roomInfo.audioChannel === 1) {
        if (!agoraStore.joined) return
        agoraStore.manageLocalAudioMicrophone(this.isOpenLocalMin && isOpenMic)
      } else if (roomInfo.audioChannel === 3) {
        if (!trtcStore.joined) return
        trtcStore.manageLocalAudioMicrophone(this.isOpenLocalMin && isOpenMic)
      }
    },
    /**
     * 设置扬声器是否打开
     */
    setIsOpenSound(value: boolean) {
      this.isOpenSound = value
    },
    /**
     * 设置麦克风是否打开
     */
    setIsOpenMic(value: boolean) {
      this.isOpenMic = value
    },
    /**
     * 设置本地麦克风是否打开
     */
    setIsOpenLocalMin(value: boolean) {
      this.isOpenLocalMin = value
    },

    // 重置数据
    resetData() {
      const roomStore = useRoomStore()
      // 强制退出才清除
      if (
        roomStore.quitRoomType === 0 ||
        roomStore.quitRoomType === 1 ||
        roomStore.quitRoomType === 3
      ) {
        this.isOpenLocalMin = true
        this.isOpenMic = true
        this.isOnMicSeat = false
        this.isManager = false
        this.micTotalCount = 10
        this.queueList = common.defaultQueueList
        this.hostMinInfo = common.defaultHostMinInfo
        this.inMicMembers = []
        this.volumeIndicatorMap = {}
        this.setIsOpenSound(
          !(common.checkVersion().ios || common.checkVersion().webView) || // eslint-disable-next-line prettier/prettier
            common.checkVersion().app
        )
      }
    }
  }
})
